'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import { FileText, ExternalLink, Clock, X } from 'lucide-react';
import StatusBadge from '@/components/ui/StatusBadge';
import { createClient } from '@/lib/supabase/client';
import { closeJobCard } from '@/lib/jobCardStore';

interface RecentJob {
  id: string;
  jobNo: string;
  vehicleReg: string;
  vin: string;
  technician: string;
  branch: string;
  partsUsed: string;
  status: 'awaiting-signature' | 'submitted' | 'pdf-generated' | 'in-progress' | 'closed';
  date: string;
}

function mapStatus(status: string, signatureData: string): RecentJob['status'] {
  if (status === 'closed') return 'closed';
  if (!signatureData || signatureData.trim() === '') return 'awaiting-signature';
  if (status === 'signed') return 'pdf-generated';
  return 'submitted';
}

export default function RecentJobCardsTable() {
  const [recentJobs, setRecentJobs] = useState<RecentJob[]>([]);
  const [loading, setLoading] = useState(true);
  const [closingId, setClosingId] = useState<string | null>(null);

  const fetchJobs = async () => {
    const supabase = createClient();
    const { data: cards, error } = await supabase
      .from('job_cards')
      .select('*')
      .order('submitted_at', { ascending: false })
      .limit(8);

    if (error) {
      console.error('RecentJobCardsTable error:', error.message);
      setLoading(false);
      return;
    }

    const cardIds = (cards || []).map((c) => c.id as string);
    let partsByCard: Record<string, string[]> = {};

    if (cardIds.length > 0) {
      const { data: parts } = await supabase
        .from('job_card_parts')
        .select('job_card_id, part_number, qty')
        .in('job_card_id', cardIds);

      (parts || []).forEach((p) => {
        const jcId = p.job_card_id as string;
        if (!partsByCard[jcId]) partsByCard[jcId] = [];
        partsByCard[jcId].push(`${p.part_number} (x${p.qty})`);
      });
    }

    const jobs: RecentJob[] = (cards || []).map((c) => {
      const partsStr = (partsByCard[c.id as string] || []).slice(0, 2).join(', ') || '—';
      const dateStr = c.submitted_at
        ? new Date(c.submitted_at as string).toLocaleDateString('en-ZA')
        : '—';
      return {
        id: c.id as string,
        jobNo: c.job_number as string,
        vehicleReg: c.vehicle_reg as string,
        vin: (c.vin as string) || '—',
        technician: c.technician as string,
        branch: c.branch as string,
        partsUsed: partsStr,
        status: mapStatus(c.status as string, c.signature_data as string),
        date: dateStr,
      };
    });

    setRecentJobs(jobs);
    setLoading(false);
  };

  const handleClose = async (id: string, jobNo: string) => {
    if (!confirm(`Close job card ${jobNo}? This marks it as completed and cannot be undone.`)) return;
    setClosingId(id);
    try {
      await closeJobCard(id);
      await fetchJobs();
    } catch (err) {
      console.error('Failed to close job card:', err);
    } finally {
      setClosingId(null);
    }
  };

  useEffect(() => {
    fetchJobs();

    const supabase = createClient();
    const channel = supabase
      .channel('recent_job_cards')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'job_cards' }, () => {
        fetchJobs();
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, []);

  return (
    <div className="card-surface">
      <div className="flex items-center justify-between px-5 py-4 border-b border-border">
        <div className="flex items-center gap-2">
          <FileText size={18} className="text-primary" />
          <h2 className="text-base font-semibold text-foreground">Recent Job Cards</h2>
          <span className="badge badge-muted text-xs">{recentJobs.length} records</span>
          <span className="flex items-center gap-1 text-xs text-positive">
            <span className="w-1.5 h-1.5 rounded-full bg-positive inline-block alert-pulse" />
            Live
          </span>
        </div>
        <Link href="/job-card-form" className="btn-primary text-xs py-1.5 px-3">
          + New Job Card
        </Link>
      </div>

      <div className="overflow-x-auto scrollbar-thin">
        {loading ? (
          <div className="px-5 py-8 text-center text-sm text-muted-foreground">Loading job cards…</div>
        ) : recentJobs.length === 0 ? (
          <div className="px-5 py-8 text-center text-sm text-muted-foreground">
            No job cards yet. <Link href="/job-card-form" className="text-primary hover:underline">Create the first one →</Link>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr style={{ backgroundColor: 'rgba(255,255,255,0.02)' }}>
                {['Job #', 'Vehicle Reg', 'VIN', 'Technician', 'Branch', 'Parts Used', 'Status', 'Date', ''].map((col) => (
                  <th
                    key={`col-${col}`}
                    className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-widest whitespace-nowrap"
                    style={{ color: 'var(--muted-foreground)', letterSpacing: '0.06em' }}
                  >
                    {col}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {recentJobs.map((job) => (
                <tr
                  key={job.id}
                  className={`hover:bg-secondary/40 transition-colors duration-100 group ${job.status === 'closed' ? 'opacity-60' : ''}`}
                >
                  <td className="px-4 py-3">
                    <span className="font-mono-data text-xs font-semibold text-primary">{job.jobNo}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="font-mono-data text-xs font-semibold text-foreground">{job.vehicleReg}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="font-mono-data text-xs text-muted-foreground">{job.vin}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs text-foreground">{job.technician}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs text-muted-foreground">{job.branch}</span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="font-mono-data text-xs text-muted-foreground">{job.partsUsed}</span>
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge status={job.status} size="sm" />
                  </td>
                  <td className="px-4 py-3">
                    <span className="flex items-center gap-1 text-xs text-muted-foreground">
                      <Clock size={11} />
                      {job.date}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity duration-150">
                      <button
                        className="p-1.5 rounded-md hover:bg-primary/10 text-muted-foreground hover:text-primary"
                        aria-label={`View job card ${job.jobNo}`}
                        title={`View full job card ${job.jobNo}`}
                      >
                        <ExternalLink size={14} />
                      </button>
                      {job.status !== 'closed' && (
                        <button
                          onClick={() => handleClose(job.id, job.jobNo)}
                          disabled={closingId === job.id}
                          className="p-1.5 rounded-md hover:bg-danger/10 text-muted-foreground hover:text-danger disabled:opacity-40 disabled:cursor-not-allowed"
                          aria-label={`Close job card ${job.jobNo}`}
                          title="Close this job card"
                        >
                          {closingId === job.id ? (
                            <span className="w-3.5 h-3.5 border border-current border-t-transparent rounded-full animate-spin inline-block" />
                          ) : (
                            <X size={14} />
                          )}
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <div className="px-5 py-3 border-t border-border flex items-center justify-between">
        <p className="text-xs text-muted-foreground">Showing {recentJobs.length} most recent job cards</p>
        <Link href="/job-card-form" className="text-xs text-primary hover:underline">
          View all job cards →
        </Link>
      </div>
    </div>
  );
}